import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Lock, MapPin, Loader2, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSubscription } from "@/context/SubscriptionContext";

const TINT = {
  sapphire: { text: "#6B9EE8", border: "rgba(15, 82, 186, 0.35)", glow: "rgba(15, 82, 186, 0.15)" },
  ruby:     { text: "#E86B8C", border: "rgba(224, 17, 95, 0.35)", glow: "rgba(224, 17, 95, 0.15)" },
  emerald:  { text: "#6BE895", border: "rgba(80, 200, 120, 0.35)", glow: "rgba(80, 200, 120, 0.15)" },
};

const cache = {};

const loadFiches = async (gem) => {
  if (cache[gem]) return cache[gem];
  const { data } = await axios.get(`/data/${gem}.json`);
  cache[gem] = data;
  return data;
};

const normalize = (s) =>
  (s || "")
    .split("(")[0]
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// EN → FR normalized aliases so EN country labels resolve to French JSON keys
const EN_TO_FR = {
  australia: "australie",
  thailand: "thailande",
  cambodia: "cambodge",
  tanzania: "tanzanie",
  brazil: "bresil",
  zambia: "zambie",
  ethiopia: "ethiopie",
  russia: "russie",
  colombia: "colombie",
  burma: "birmanie",
  ceylon: "ceylan",
  nigeria: "nigeria",
  afghanistan: "afghanistan",
  madagascar: "madagascar",
  vietnam: "vietnam",
  kenya: "kenya",
  mozambique: "mozambique",
  pakistan: "pakistan",
  kashmir: "kashmir",
  montana: "montana",
  china: "chine",
  india: "inde",
  egypt: "egypte",
  tajikistan: "tadjikistan",
  malawi: "malawi",
};

const findFiche = (fichesObj, country) => {
  if (!fichesObj) return null;
  if (fichesObj[country]) return fichesObj[country];
  const target = normalize(country);
  const alias = EN_TO_FR[target] || target;
  for (const k of Object.keys(fichesObj)) {
    const nk = normalize(k);
    if (nk === target || nk === alias) return fichesObj[k];
  }
  return null;
};

export const CountryFicheModal = ({ open, onClose, gemType, country, onSubscribe }) => {
  const { t, lang } = useLang();
  const { subscribed } = useSubscription();
  const [fiches, setFiches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);

  useEffect(() => {
    if (!open || !gemType) return;
    setLoading(true);
    loadFiches(gemType).then((d) => { setFiches(d); setLoading(false); })
      .catch(() => { setFiches(null); setLoading(false); });
  }, [open, gemType]);

  useEffect(() => { if (!open) setZoomOpen(false); }, [open]);

  if (!open) return null;
  const tint = TINT[gemType] || TINT.sapphire;
  const fiche = findFiche(fiches?.fiches, country);
  const zoomSrc = fiche ? ((lang === "en" && fiche.image_en) ? fiche.image_en : fiche.image) : null;

  return (
    <div
      data-testid="country-fiche-modal"
      className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-8 bg-black/85 backdrop-blur-md overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl my-6 rounded-sm bg-[#050B1E] border p-4 md:p-10"
        style={{ borderColor: tint.border, boxShadow: `inset 0 0 120px ${tint.glow}` }}
      >
        <button
          data-testid="fiche-close-btn"
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors z-10"
          aria-label={t.modal.close}
        >
          <X size={22} strokeWidth={1.4} />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <MapPin size={20} strokeWidth={1.4} style={{ color: tint.text }} />
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661]">
              {t.gems[gemType]}
            </div>
            <h2 className="font-serif text-2xl md:text-4xl font-light text-[#F8F5EE] leading-tight">              {(lang === "en" && fiche?.title_en) ? fiche.title_en : (fiche?.title || country)}
            </h2>
          </div>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="animate-spin text-[#C1A661]" size={28} />
          </div>
        ) : !fiche ? (
          <div className="py-10 text-center text-[#A1A7B3] text-sm">{t.fiche.notFound}</div>
        ) : subscribed ? (
          <SubscribedContent fiche={fiche} lang={lang} t={t} tint={tint} onZoom={() => setZoomOpen(true)} />
        ) : (
          <LockedContent fiche={fiche} lang={lang} t={t} tint={tint} onSubscribe={onSubscribe} />
        )}
      </div>

      {zoomOpen && zoomSrc && (
        <div
          data-testid="fiche-zoom-overlay"
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col"
          onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
        >
          <div className="flex items-center justify-between p-4 border-b border-[#C1A661]/20 bg-[#050B1E]/95">
            <button
              data-testid="fiche-zoom-back-btn"
              onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
              className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#C1A661]/40 hover:border-[#C1A661] text-[#E2CFA4] hover:text-[#F8F5EE] text-xs tracking-[0.25em] uppercase transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              <span>{lang === "fr" ? "Retour" : "Back"}</span>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setZoomOpen(false); }}
              className="text-[#A1A7B3] hover:text-[#F8F5EE] transition-colors"
              aria-label={t.modal.close}
              data-testid="fiche-zoom-close-btn"
            >
              <X size={22} strokeWidth={1.4} />
            </button>
          </div>
          <div
            className="flex-1 overflow-auto p-4 md:p-8 flex items-start justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={zoomSrc}
              alt={fiche?.title || country}
              className="max-w-full h-auto rounded-sm shadow-2xl"
              style={{ backgroundColor: "#F8F5EE" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SubscribedContent = ({ fiche, lang, t, tint, onZoom }) => {
  const imgSrc = (lang === "en" && fiche.image_en) ? fiche.image_en : fiche.image;
  // If PDF image is available, display it verbatim (source of truth).
  if (imgSrc) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={onZoom}
          className="block w-full text-left"
          data-testid="fiche-image-btn"
          aria-label={t.fiche.viewFullSize}
        >
          <img
            src={imgSrc}
            alt={fiche.title}
            data-testid="fiche-image"
            className="w-full h-auto rounded-sm border cursor-zoom-in hover:brightness-105 transition"
            style={{ borderColor: tint.border, backgroundColor: "#F8F5EE" }}
            loading="lazy"
          />
        </button>
        <button
          type="button"
          onClick={onZoom}
          data-testid="fiche-image-fullsize"
          className="inline-block text-[10px] tracking-[0.25em] uppercase text-[#C1A661] hover:text-[#E2CFA4] transition-colors"
        >
          {t.fiche.viewFullSize || "Voir en plein écran"} ↗
        </button>
      </div>
    );
  }
  // Fallback: legacy text rendering when no image is available.
  return (
    <div className="space-y-6">
      <div className="text-[13px] leading-relaxed text-[#D8DBE2] font-light whitespace-pre-line">
        {fiche.description}
      </div>
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#C1A661] mb-3">
          {t.fiche.pricingTitle}
        </div>
        <div className="overflow-x-auto border border-[#C1A661]/20 rounded-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#C1A661]/20 bg-[#080C17]">
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#C1A661] font-medium">{t.fiche.caratRange}</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#C1A661] font-medium">AAA</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#C1A661] font-medium">AA</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#C1A661] font-medium">A</th>
              </tr>
            </thead>
            <tbody>
              {fiche.pricing.map((row, i) => (
                <tr key={i} className="border-b border-[#C1A661]/10 last:border-b-0">
                  <td className="px-4 py-3 text-[#E2CFA4] font-medium">{row.carat}</td>
                  <td className="px-4 py-3 text-[#F8F5EE] font-light">{row.AAA}</td>
                  <td className="px-4 py-3 text-[#D8DBE2] font-light">{row.AA}</td>
                  <td className="px-4 py-3 text-[#A1A7B3] font-light">{row.A}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const LockedContent = ({ fiche, lang, t, tint, onSubscribe }) => {
  const title = (lang === "en" && fiche.title_en) ? fiche.title_en : fiche.title;
  const description = fiche.description;
  const rows = Array.isArray(fiche.pricing) ? fiche.pricing : [];
  const headers = lang === "fr"
    ? {
        carat: { short: "Carats", long: "Poids (carats)" },
        aaa:   { short: "AAA",    long: "AAA — Exceptionnel" },
        aa:    { short: "AA",     long: "AA — Très belle" },
        a:     { short: "A",      long: "A — Belle qualité" },
      }
    : {
        carat: { short: "Carats", long: "Stone weight" },
        aaa:   { short: "AAA",    long: "AAA — Exceptional" },
        aa:    { short: "AA",     long: "AA — Very Fine" },
        a:     { short: "A",      long: "A — Fine Quality" },
      };
  const legend = lang === "fr"
    ? "Référence de marché — pierres taillées naturelles — prix au carat en euros"
    : "Indicative market reference — natural cut stones — prices per carat in euros";

  const Header = ({ h, first = false }) => (
    <th className={`text-left px-2 md:px-4 py-2 md:py-3 text-[9px] md:text-[10px] tracking-[0.14em] md:tracking-[0.18em] uppercase text-[#E2CFA4] font-medium border-b border-[#C1A661]/20 ${first ? "" : "border-l"}`}>
      <span className="md:hidden">{h.short}</span>
      <span className="hidden md:inline">{h.long}</span>
    </th>
  );

  return (
    <div className="space-y-6" data-testid="fiche-locked-content">
      {/* Fiche header — fully readable */}
      <div>
        <h3 className="font-serif text-xl md:text-2xl text-[#F8F5EE] font-light leading-tight mb-1">
          {title}
        </h3>
        <p className="text-[11px] md:text-[12px] italic text-[#A1A7B3] font-light">{legend}</p>
      </div>

      {/* Pricing table — structure visible, only price values blurred */}
      <div className="border border-[#C1A661]/25 rounded-sm overflow-x-auto">
        <table className="w-full text-xs md:text-sm border-collapse">
          <thead>
            <tr className="bg-[#0B1128]">
              <Header h={headers.carat} first />
              <Header h={headers.aaa} />
              <Header h={headers.aa} />
              <Header h={headers.a} />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-[#C1A661]/10 last:border-b-0">
                <td className="px-2 md:px-4 py-2 md:py-3 text-[11px] md:text-sm text-[#F8F5EE] font-medium whitespace-nowrap">{row.carat}</td>
                <td className="px-2 md:px-4 py-2 md:py-3 border-l border-[#C1A661]/10">
                  <span className="inline-block filter blur-[6px] select-none text-[11px] md:text-sm">{row.AAA}</span>
                </td>
                <td className="px-2 md:px-4 py-2 md:py-3 border-l border-[#C1A661]/10">
                  <span className="inline-block filter blur-[6px] select-none text-[11px] md:text-sm">{row.AA}</span>
                </td>
                <td className="px-2 md:px-4 py-2 md:py-3 border-l border-[#C1A661]/10">
                  <span className="inline-block filter blur-[6px] select-none text-[11px] md:text-sm">{row.A}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Description — fully readable */}
      {description && (
        <div className="text-[13px] leading-relaxed text-[#D8DBE2] font-light whitespace-pre-line">
          {description}
        </div>
      )}
    </div>
  );
};
